import { faChevronDown, faChevronUp, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { CloseButton } from "../components/CloseButton";
import { SourceLayer, useLayers } from "../state";

type LayersProps = {
  layers?: SourceLayer[];
  selectedId?: string | null;
  onSelect?: (layer: SourceLayer) => void;
};

// w-full border-2 border-solid border-gray-300 rounded-lg
const PreviewThumb = ({ selected, layer }: { selected: boolean; layer: SourceLayer }) => (
  <div
    className={`w-full h-24 rounded bg-white bg-center bg-cover ${
      selected ? "ring-2 ring-sidebar-menu-active" : "border border-sidebar-menu-border"
    }`}
    style={{ backgroundImage: `url(${layer.icon})` }}
  />
);

interface LayerGroupBlockProperties {
  layers: SourceLayer[];
  selected: SourceLayer[];
  handleSelect: (l: SourceLayer) => void;
}
interface LayerGroupProperties extends LayerGroupBlockProperties {
  title: string;
  open: boolean;
  onClick: (e) => void;
}

const LayerGroupBlock = ({ layers, selected, handleSelect }) => (
  <ul className="pl-2 grid grid-cols-3 items-start">
    {layers.map((layer: SourceLayer) => {
      const isSelected = selected.find((l) => l.name === layer.name);
      return (
        <li key={layer.name} className="flex box-content p-1 w-32">
          <button
            className={
              "w-full text-center gap-1 rounded-md p-2 my-1 " +
              (isSelected
                ? "bg-sidebar-menu-active text-white"
                : "hover:bg-sidebar-menu-active/10 text-sidebar-menu-text")
            }
            onClick={() => handleSelect(layer)}
          >
            <PreviewThumb selected={isSelected} layer={layer} />
            <div className="text-sm pt-1">{layer.title}</div>
          </button>
        </li>
      );
    })}
  </ul>
);

const LayerGroup = ({
  title,
  layers,
  open,
  onClick,
  selected,
  handleSelect,
}: LayerGroupProperties) => {
  const selectionCount =
    title !== "Achtergrond" &&
    layers.filter((l) => selected.find((layer) => l.name === layer.name)).length;
  return (
    <li>
      <button
        className={
          "w-full flex items-center justify-between p-3 hover:bg-sidebar-menu-active/10 hover:border-sidebar-menu-active/10 border-2 " +
          (selectionCount ? "border-sidebar-menu-active/40" : "border-transparent")
        }
        onClick={onClick}
        aria-expanded={open}
      >
        <span className="text-[15px] text-sidebar-menu-text">{title}</span>
        <span className="text-dark">
          {selectionCount > 0 && (
            <span className="inline-flex items-center rounded-xl bg-green-400/10 px-2 py text-xs font-medium text-green-400 inset-ring inset-ring-green-500/20">
              {selectionCount}
            </span>
          )}
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className="inline w-6 h-6"
            fill="currentColor"
          />
        </span>
      </button>
      {open && <LayerGroupBlock layers={layers} selected={selected} handleSelect={handleSelect} />}
    </li>
  );
};

export const Layers = ({ onSelect }: LayersProps) => {
  const { layers, fetchLayers } = useLayers();
  useEffect(() => {
    fetchLayers();
  }, [layers, fetchLayers]);
  if (!layers) return null;
  return <Layers2 layers={layers} onSelect={onSelect} />;
};

export const Layers2 = ({ layers, onSelect }: LayersProps) => {
  const data = layers;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(data.map((g) => [g.group, false]))
  );
  const [filtered, setFiltered] = useState<string>("");
  const { toggle, selected, backgroundId, setBackground, setAll } = useLayers();

  const groups = data.reduce(
    (memo, layer) => {
      if (!layer.name.toLowerCase().includes(filtered) || layer.group == "Achtergrond") return memo;
      if (!memo[layer.group]) memo[layer.group] = [];
      memo[layer.group].push(layer);
      return memo;
    },
    {} as Map<string, string[]>
  );

  const onChangeFiltered = useCallback(
    (e) => {
      setFiltered(e.target.value.toLowerCase());
    },
    [setFiltered]
  );
  const handleToggle = useCallback(
    (groupId: string) => setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] })),
    [setOpenGroups]
  );
  const handleSelect = useCallback(
    (layer: SourceLayer) => {
      toggle(layer);
      onSelect?.(layer);
    },
    [toggle, onSelect]
  );

  const onReset = useCallback(() => {
    setOpenGroups({});
    setAll([]);
  }, [setOpenGroups, setAll]);

  const groupList = Object.entries(groups);

  return (
    <div className="fixed left-10 sm:left-20 my-14 pb-8 bg-sidebar-menu border-r border-r-sidebar-menu-border h-screen z-10 w-[calc(100vw-5rem)] sm:w-[28rem] max-w-[32rem] overflow-y-auto">
      <div className="px-6 pt-6 pb-4 border-b border-sidebar-menu-border">
        <CloseButton className="text-black hover:text-gray hover:bg-black" />
        <div className="flex flex-row mb-2">
          <h1 className="basis-md text-2xl font-semibold">Kaartlagen</h1>
          {selected.length > 0 && (
            <button title="Reset lagen" onClick={onReset} className="text-sm text-dark/70 pr-3">
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}
        </div>
        <label className="block">
          <span className="sr-only">Zoeken</span>
          <input
            type="search"
            placeholder="Zoek in kaartlagen"
            className="w-full rounded-md px-4 py-2 text-sm bg-white border border-sidebar-menu-border placeholder:text-gray-400"
            onKeyUp={onChangeFiltered}
          />
        </label>
      </div>
      <ul className="p-2 divide-y divide-sidebar-menu-border">
        <LayerGroup
          title="Achtergrond"
          layers={data.filter((l) => l.group === "Achtergrond")}
          open={!!openGroups["Achtergrond"]}
          onClick={() => handleToggle("Achtergrond")}
          // @ts-ignore
          selected={[{ name: backgroundId }]}
          handleSelect={(l) => setBackground(l.name)}
        />

        {groupList.map(([groupId, layers]) => (
          <LayerGroup
            key={groupId}
            title={groupId}
            layers={layers}
            open={filtered.length > 2 || !!openGroups[groupId]}
            onClick={() => handleToggle(groupId)}
            selected={selected}
            handleSelect={handleSelect}
          />
        ))}
        {!groupList.length && <div>Geen resultaten</div>}
      </ul>
    </div>
  );
};
